import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import TimestampRefreshButton, {
  TimestampRefreshWrapper,
} from '#/shared/components/TimestampRefreshButton';
import ErrorWithCountdown from '#/shared/components/ErrorWithCountdown';
import StopDetailHeader from '#/transit-tracker/components/StopDetailHeader';
import StopSignList from '#/transit-tracker/components/StopSignList';
import useFavoriteStops from '#/transit-tracker/hooks/useFavoriteStops';
import StopAmenitiesList from '#/transit-tracker/components/StopAmenitiesList';
import DetailViewLayout from '#/components/DetailViewLayout';
import DetailNavHeader from '#/shared/components/DetailNavHeader';
import SEO from '#/shared/components/SEO';
import useCurrentQuery from '#/trip-planner/hooks/useCurrentQuery';
import { trimetLocationToOtpLocation } from '#/trip-planner/queries';
import ROUTES from '#/constants/routes';
import useNavigate from '#/shared/hooks/useNavigate';
import useViewMapControls from '#/map/hooks/useViewMapControls';
import useCurrentQueryRedirect from '#/shared/hooks/useCurrentQueryRedirect';
import {
  ORIGIN_LOCATION_TYPE,
  DESTINATION_LOCATION_TYPE,
  ARRIVALS_REFRESH_INTERVAL,
} from '#/shared/constants';
import detectOldIOS from '#/shared/utils/detect-old-ios';
import useTriMetStaticPageLinks from '#/shared/hooks/useTriMetStaticPageLinks';
import { useGetStopArrivalsQuery } from '#/services/developerApi';
import { castDigitsToInt } from '#/shared/utils/string';

const MEDALLIA_SURVEY_LINK_TEMPLATE =
  'https://survey-usw1-2.medallia.com/?trimet&type=1&unit=stop_';

function StopDetailContainer({ stopId: rawStopId, routeId }) {
  // TODO: Add handling that validates the supplied stop id, which
  //  comes from the app url, and notifies the user if it's invalid:
  //  https://trimet.atlassian.net/browse/PUBWEB-1721
  const stopId = castDigitsToInt(rawStopId);

  const {
    navigate,
    navigateToStopAndSign,
    generateNavigateBack,
    generateNavigateClose,
  } = useNavigate();

  const {
    data: selectedStop,
    refetch: refetchStopArrivals,
    fulfilledTimeStamp: stopArrivalsTimestamp,
  } = useGetStopArrivalsQuery(stopId, {
    pollingInterval: ARRIVALS_REFRESH_INTERVAL,
  });

  const { toggleFavoriteStop, isFavoriteRouteStop } = useFavoriteStops();
  const { setCurrentQueryParam } = useCurrentQuery();
  const { stopDetailViewOpened, viewClosed } = useViewMapControls();
  const { currentQueryRedirect } = useCurrentQueryRedirect();

  const [amenitiesVisible, setAmenitiesVisible] = useState(false);
  const [lastStopId, setLastStopId] = useState(stopId);

  const stopAttributes = selectedStop?.stop;
  const stopAmenities = stopAttributes?.amenity;
  const isArrivalsError = useSelector(s => s.transitTracker.isArrivalsError);

  useEffect(() => {
    // if the user selects another stop while this view is active
    // and stop amenities are visible hide them for the new stop
    if (stopId !== lastStopId && amenitiesVisible) {
      setAmenitiesVisible(false);
    }

    setLastStopId(stopId);
  }, [stopId, lastStopId, amenitiesVisible]);

  useEffect(() => {
    stopDetailViewOpened();
    return viewClosed;
  }, [stopDetailViewOpened, viewClosed]);

  const navigateToPlanner = useCallback(() => {
    navigate(ROUTES.planner);
  }, [navigate]);

  const navigateToTrip = useCallback(() => {
    navigate(ROUTES.trip);
  }, [navigate]);

  const navigateBack = amenitiesVisible
    ? () => setAmenitiesVisible(false)
    : generateNavigateBack([], ROUTES.home);

  const navigateClose = amenitiesVisible
    ? null
    : generateNavigateClose([ROUTES.trip, ROUTES.routeById]);

  const onStopAmenitiesMenuItemClick = useCallback(() => {
    setAmenitiesVisible(true);
  }, []);

  const { getStopScheduleLink } = useTriMetStaticPageLinks();

  const getSurveyLink = () =>
    `${MEDALLIA_SURVEY_LINK_TEMPLATE}${parseInt(stopId, 10)}`;

  const navigateToStopSchedule = (stopRouteId = null) => {
    window.open(getStopScheduleLink(stopId, stopRouteId), '_blank');
  };

  const navigateToVehicleTracker = (selectedRouteId, blockId) =>
    selectedRouteId && blockId
      ? navigate(
          ROUTES.vehicleWithSelectedStop,
          selectedRouteId,
          blockId,
          stopId
        )
      : null;

  const navigateToRouteAlerts = id => navigate(ROUTES.alertsById, id);

  const onLocationSelected = useCallback(
    locType => {
      setCurrentQueryParam(
        locType,
        trimetLocationToOtpLocation({ ...stopAttributes })
      );
      currentQueryRedirect(locType, navigateToPlanner, navigateToTrip);
    },
    [
      setCurrentQueryParam,
      stopAttributes,
      currentQueryRedirect,
      navigateToPlanner,
      navigateToTrip,
    ]
  );

  const navHeaderMessageId = `detailview.${
    amenitiesVisible ? 'stopamenitiestitleid' : 'stoptitleid'
  }`;

  return (
    <>
      {stopId && stopAttributes && (
        <SEO
          metadataId="stopDetail"
          dynamicValues={{
            stopId,
            stopName: stopAttributes.desc,
          }}
        />
      )}
      <DetailViewLayout
        top={
          <DetailNavHeader
            onBack={navigateBack}
            onClose={navigateClose}
            content={
              stopId && (
                <FormattedMessage
                  id={navHeaderMessageId}
                  values={{ id: stopId }}
                />
              )
            }
          />
        }
        middle={
          !amenitiesVisible &&
          !isArrivalsError && (
            <StopDetailHeader
              stop={stopAttributes}
              navigateToTripPlannerTo={() => {
                onLocationSelected(DESTINATION_LOCATION_TYPE);
              }}
              navigateToTripPlannerFrom={() => {
                onLocationSelected(ORIGIN_LOCATION_TYPE);
              }}
              showInfoIcon={!!stopAmenities && stopAmenities.length > 0}
              onStopAmenitiesClick={onStopAmenitiesMenuItemClick}
              surveyLink={getSurveyLink()}
            />
          )
        }
        bottom={
          (isArrivalsError && (
            <ErrorWithCountdown
              refreshInterval={ARRIVALS_REFRESH_INTERVAL}
              errorMessageId="arrivals.error"
            />
          )) ||
          (!amenitiesVisible ? (
            <>
              <TimestampRefreshWrapper needsIOSStyles={detectOldIOS()}>
                <TimestampRefreshButton
                  onClick={refetchStopArrivals}
                  timestamp={stopArrivalsTimestamp}
                />
              </TimestampRefreshWrapper>
              <StopSignList
                stop={stopAttributes}
                signs={selectedStop?.signs}
                toggleFavoriteStop={toggleFavoriteStop}
                isFavoriteStop={isFavoriteRouteStop}
                navigateToVehicleTracker={navigateToVehicleTracker}
                navigateToSchedule={navigateToStopSchedule}
                navigateToRouteAlerts={navigateToRouteAlerts}
                navigateToStopAndSign={navigateToStopAndSign}
                routeId={routeId}
              />
            </>
          ) : (
            stopAmenities && <StopAmenitiesList amenities={stopAmenities} />
          ))
        }
      />
    </>
  );
}

StopDetailContainer.propTypes = {
  stopId: PropTypes.string,
  routeId: PropTypes.string,
};

StopDetailContainer.defaultProps = {
  stopId: null,
  routeId: null,
};

export default StopDetailContainer;
